import React from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle, ShieldCheck, Navigation, Car } from "lucide-react";
import { clinic } from "../mock";
import { useBooking } from "../App";
import { Button } from "./ui/button";

export default function Contact() {
  const { openBooking } = useBooking();
  const info = [
    {
      icon: MapPin,
      label: "Dirección",
      value: clinic.address,
      note: clinic.addressNote,
    },
    { icon: Phone, label: "Call Center", value: clinic.callCenter, href: `tel:${clinic.callCenterTel}` },
    { icon: MessageCircle, label: "WhatsApp", value: clinic.whatsapp, href: clinic.whatsappLink },
    { icon: Mail, label: "Correo", value: clinic.email, href: `mailto:${clinic.email}` },
  ];

  return (
    <section id="contacto" className="bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="max-w-2xl reveal">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1d6fc0]">
            Contacto
          </span>
          <h2 className="font-display font-extrabold text-3xl lg:text-[2.6rem] leading-tight mt-4 text-[#0b2545]">
            Estamos cerca de ti en San Fernando
          </h2>
          <p className="text-slate-500 mt-4 leading-relaxed">
            Encuentra toda la información para visitarnos o reservar tu hora con claridad.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mt-12">
          <div className="reveal space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              {info.map((it) => {
                const Card = (
                  <div className="card-hover h-full rounded-2xl border border-slate-200 p-6 bg-gradient-to-b from-white to-slate-50/60">
                    <div className="w-11 h-11 rounded-xl bg-[#e8f2fd] grid place-items-center text-[#1d6fc0]">
                      <it.icon size={20} />
                    </div>
                    <div className="text-xs font-semibold tracking-wide uppercase text-slate-400 mt-4">
                      {it.label}
                    </div>
                    <div className="font-medium text-[#0b2545] mt-1 break-words">{it.value}</div>
                    {it.note && <div className="text-sm text-slate-500 mt-0.5">{it.note}</div>}
                  </div>
                );
                return it.href ? (
                  <a
                    key={it.label}
                    href={it.href}
                    target={it.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="block"
                  >
                    {Card}
                  </a>
                ) : (
                  <div key={it.label}>{Card}</div>
                );
              })}
            </div>

            <div className="rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 text-[#0b2545] font-display font-bold">
                <Clock size={18} className="text-[#1d6fc0]" /> Horario de atención
              </div>
              <div className="mt-4 divide-y divide-slate-100">
                {clinic.hours.map((h) => (
                  <div key={h.day} className="flex items-center justify-between py-2.5 text-sm">
                    <span className="text-slate-600">{h.day}</span>
                    <span className="font-medium text-[#0b2545]">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={openBooking}
                className="flex-1 rounded-full bg-[#1d6fc0] hover:bg-[#175a9e] h-12 text-base font-semibold"
              >
                Reservar Hora
              </Button>
              <a href={clinic.panelUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button
                  variant="outline"
                  className="w-full rounded-full h-12 text-base font-semibold border-slate-300 text-[#0b2545] hover:border-[#1d6fc0] hover:text-[#1d6fc0]"
                >
                  <ShieldCheck size={18} className="mr-2" /> Panel Clínico
                </Button>
              </a>
            </div>
          </div>

          <div className="reveal">
            <div className="rounded-2xl overflow-hidden border border-slate-200 min-h-[420px] shadow-lg shadow-blue-950/5">
              <iframe
                title="Ubicación Centro Oftalmológico Santa Lucía"
                src={clinic.mapEmbed}
                className="w-full h-full min-h-[420px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <a href={clinic.directionsUrl} target="_blank" rel="noopener noreferrer">
                <Button className="w-full sm:w-auto rounded-full bg-[#1d6fc0] hover:bg-[#175a9e] h-11 px-6 font-semibold">
                  <Navigation size={17} className="mr-2" /> Cómo llegar
                </Button>
              </a>
              <div className="flex items-start gap-2 text-sm text-slate-500">
                <Car size={17} className="text-[#1d6fc0] shrink-0 mt-0.5" />
                {clinic.parkingNote}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
